import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { api, apiError } from "@/lib/api";
import { Field } from "@/components/Field";
import { DocPicker } from "@/components/apply/DocPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPLOYMENT = ["Employed", "Self-employed", "Retired", "Unemployed", "Other"];
const REQUIRED = { name: "Full name is required", phone: "Phone is required", address: "Address is required", purpose: "Loan purpose is required",
  gross_monthly_income: "This field is required", housing_payment: "This field is required" };

const Section = ({ title, children, delay }) => (
  <section className="fade-up rounded-lg border bg-card p-5 sm:p-7" style={{ animationDelay: delay }}>
    <h3 className="text-lg font-semibold">{title}</h3>
    <div className="mt-5 grid gap-5 sm:grid-cols-2">{children}</div>
  </section>
);

export default function ApplyForm() {
  const { user } = useApp();
  const [f, setF] = useState({ name: "", email: "", phone: "", address: "", amount: "", purpose: "", term_months: "", employment_status: "",
    employer: "", job_title: "", years_at_employer: "", gross_monthly_income: "", other_monthly_income: "", housing_payment: "" });
  const [docs, setDocs] = useState({ id: [], paystub: [], other: [] });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");

  useEffect(() => {
    if (user) setF((s) => ({ ...s, name: s.name || user.name || "", email: user.email, phone: s.phone || user.phone || "", address: s.address || user.address || "" }));
  }, [user]);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const inp = (k, props = {}) => (
    <Input id={k} value={f[k]} onChange={set(k)} className={`h-11 bg-background ${errors[k] ? "border-destructive" : ""}`} data-testid={`apply-${k.replace(/_/g, "-")}-input`} {...props} />
  );

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    Object.entries(REQUIRED).forEach(([k, m]) => { if (!String(f[k]).trim()) errs[k] = m; });
    if (!(+f.amount > 0)) errs.amount = "Amount must be greater than 0";
    if (!Number.isInteger(+f.term_months) || +f.term_months < 1) errs.term_months = "Term must be at least 1 month";
    if (!f.employment_status) errs.employment_status = "Choose an employment status";
    if (!docs.id.length) errs.id_document = "Government photo ID is required";
    if (!docs.paystub.length) errs.paystubs = "Upload 1–5 pay stubs or proof of income";
    setErrors(errs);
    if (Object.keys(errs).length) { toast.error("Please fix the highlighted fields"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/applications", { ...f, file_ids: [...docs.id, ...docs.paystub, ...docs.other].map((d) => d.id) });
      setDone(data.message);
      window.scrollTo({ top: 0 });
    } catch (err) {
      const { message, fields } = apiError(err);
      setErrors(fields);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  if (done) return (
    <div className="fade-up mx-auto max-w-lg rounded-lg border bg-card p-8 text-center" data-testid="apply-success">
      <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
      <h1 className="mt-4 text-2xl font-semibold" data-testid="apply-success-message">{done}</h1>
      <p className="mt-2 text-sm text-muted-foreground">We emailed you a confirmation. You can track the status in your portal.</p>
      <Button asChild className="mt-6" data-testid="apply-back-to-portal"><Link to="/portal">Go to my portal</Link></Button>
    </div>
  );

  return (
    <form onSubmit={submit} noValidate className="space-y-6" data-testid="apply-form">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">Loan application</h1>
        <div className="gold-rule mt-4 w-24" />
      </div>
      <Section title="Basics">
        <Field label="Full name" name="name" error={errors.name}>{inp("name")}</Field>
        <Field label="Email" name="email" error={errors.email}>{inp("email", { disabled: true })}</Field>
        <Field label="Phone" name="phone" error={errors.phone}>{inp("phone", { type: "tel" })}</Field>
        <Field label="Amount requested (USD)" name="amount" error={errors.amount}>{inp("amount", { inputMode: "decimal", placeholder: "15000" })}</Field>
        <Field label="Address" name="address" error={errors.address} className="sm:col-span-2">
          <Textarea id="address" rows={2} value={f.address} onChange={set("address")} className={errors.address ? "border-destructive" : ""} data-testid="apply-address-input" />
        </Field>
        <Field label="Loan purpose" name="purpose" error={errors.purpose}>{inp("purpose", { placeholder: "e.g. Home renovation" })}</Field>
        <Field label="Requested term (months)" name="term_months" error={errors.term_months}>{inp("term_months", { inputMode: "numeric", placeholder: "36" })}</Field>
      </Section>
      <Section title="Income & employment" delay="60ms">
        <Field label="Employment status" name="employment_status" error={errors.employment_status}>
          <Select value={f.employment_status} onValueChange={set("employment_status")}>
            <SelectTrigger className={`h-11 bg-background ${errors.employment_status ? "border-destructive" : ""}`} data-testid="apply-employment-status-select"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>{EMPLOYMENT.map((x) => <SelectItem key={x} value={x} data-testid={`employment-option-${x}`}>{x}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Employer name" name="employer" error={errors.employer}>{inp("employer")}</Field>
        <Field label="Job title" name="job_title" error={errors.job_title}>{inp("job_title")}</Field>
        <Field label="Years at employer" name="years_at_employer" error={errors.years_at_employer}>{inp("years_at_employer", { inputMode: "decimal" })}</Field>
        <Field label="Gross monthly income (USD)" name="gross_monthly_income" error={errors.gross_monthly_income}>{inp("gross_monthly_income", { inputMode: "decimal" })}</Field>
        <Field label="Other monthly income (USD)" name="other_monthly_income" error={errors.other_monthly_income}>{inp("other_monthly_income", { inputMode: "decimal", placeholder: "0" })}</Field>
        <Field label="Monthly housing payment (USD)" name="housing_payment" error={errors.housing_payment}>{inp("housing_payment", { inputMode: "decimal" })}</Field>
      </Section>
      <section className="fade-up rounded-lg border bg-card p-5 sm:p-7" style={{ animationDelay: "120ms" }}>
        <h3 className="text-lg font-semibold">Documents</h3>
        <p className="mt-1 text-xs text-muted-foreground">PDF, JPG, PNG or HEIC · up to 10 MB each</p>
        <div className="mt-5 grid gap-5 md:grid-cols-3">
          <DocPicker kind="id" label="Government photo ID (required)" max={2} files={docs.id} onChange={(v) => setDocs((d) => ({ ...d, id: v }))} error={errors.id_document} testid="doc-id" />
          <DocPicker kind="paystub" label="Pay stubs / proof of income (1–5)" max={5} files={docs.paystub} onChange={(v) => setDocs((d) => ({ ...d, paystub: v }))} error={errors.paystubs} testid="doc-paystub" />
          <DocPicker kind="other" label="Other documents (optional)" max={5} files={docs.other} onChange={(v) => setDocs((d) => ({ ...d, other: v }))} error={errors.other_documents} testid="doc-other" />
        </div>
      </section>
      <div className="flex justify-end">
        <Button type="submit" disabled={busy} className="h-11 w-full sm:w-auto sm:px-10" data-testid="apply-submit-button">{busy && <Loader2 className="animate-spin" />} Submit application</Button>
      </div>
    </form>
  );
}
